import { LOCALE_DISPLAY_NAMES, DEFAULT_LOCALE, type Locale } from './i18n';
import { shouldAutoTranslate, type TranslationPreferences } from './translation-preferences';

/**
 * Picks the viewer-locale version of stored user-generated content (posts, comments,
 * profiles, …) out of the `translations[]` array the API returns, or falls back to the
 * original. Framework-free so every surface resolves identically — town-square renders
 * community feeds with it, and the client's notification post modal reuses the same rule.
 *
 * `preferences` is a CLIENT-ONLY input. Server components in town-square render into a
 * shared ISR cache (`revalidate = 60` / `3600`), so feeding one viewer's preference into
 * them would serve that viewer's choice to everybody who hits the cached page. Server
 * callers pass nothing and keep the always-translate default; only client components read
 * the viewer's preference (town-square via `useTranslationPreferences()`, the client via
 * its profile prefs / react-i18next locale).
 */

interface Translation {
  locale: string;
  title?: string | null;
  content?: string | null;
  status: string;
}

interface TranslatableEntity {
  title?: string | null;
  content?: string | null;
  sourceLocale?: string | null;
  translations?: Translation[];
}

interface ResolvedContent {
  title?: string | null;
  content?: string | null;
  isTranslated: boolean;
  sourceLocale?: string;
  sourceDisplayName?: string;
  original: { title?: string | null; content?: string | null };
}

export function resolveTranslation(
  entity: TranslatableEntity,
  userLocale: string,
  preferences?: TranslationPreferences | null
): ResolvedContent {
  const original = { title: entity.title, content: entity.content };
  const sourceLocale = entity.sourceLocale ?? DEFAULT_LOCALE;

  if (sourceLocale === userLocale) {
    return { ...original, isTranslated: false, original };
  }

  if (!shouldAutoTranslate(sourceLocale, preferences)) {
    return { ...original, isTranslated: false, original };
  }

  const translation = entity.translations?.find(
    (t) => t.locale === userLocale && (t.status === 'completed' || t.status === 'stale')
  );

  if (!translation) {
    return { ...original, isTranslated: false, original };
  }

  return {
    title: translation.title ?? original.title,
    content: translation.content ?? original.content,
    isTranslated: true,
    sourceLocale,
    sourceDisplayName: LOCALE_DISPLAY_NAMES[sourceLocale as Locale],
    original,
  };
}

export interface ResolvedEntityContent<F extends string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields: Record<F, any>;
  isTranslated: boolean;
  sourceLocale?: string;
  sourceDisplayName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  original: Record<F, any>;
}

export function resolveEntityTranslation<F extends string>(
  entity: {
    sourceLocale?: string | null;
    translations?: Array<{ locale: string; status: string } & Record<string, unknown>>;
  },
  userLocale: string,
  fieldNames: readonly F[],
  preferences?: TranslationPreferences | null
): ResolvedEntityContent<F> {
  const rec = entity as Record<string, unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const original = {} as Record<F, any>;
  for (const f of fieldNames) {
    original[f] = rec[f];
  }

  const sourceLocale = entity.sourceLocale ?? DEFAULT_LOCALE;

  if (sourceLocale === userLocale) {
    return { fields: { ...original }, isTranslated: false, original };
  }

  if (!shouldAutoTranslate(sourceLocale, preferences)) {
    return { fields: { ...original }, isTranslated: false, original };
  }

  const translation = entity.translations?.find(
    (t) => t.locale === userLocale && (t.status === 'completed' || t.status === 'stale')
  );

  if (!translation) {
    return { fields: { ...original }, isTranslated: false, original };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resolved = {} as Record<F, any>;
  for (const f of fieldNames) {
    resolved[f] = translation[f] ?? original[f];
  }

  return {
    fields: resolved,
    isTranslated: true,
    sourceLocale,
    sourceDisplayName: LOCALE_DISPLAY_NAMES[sourceLocale as Locale],
    original,
  };
}

export function resolveProfileBio(
  profile: {
    bio?: string | null;
    sourceLocale?: string | null;
    translations?: Array<{ locale: string; status: string; bio?: string | null }>;
  },
  userLocale: string,
  preferences?: TranslationPreferences | null
): string | null | undefined {
  return resolveEntityTranslation(profile, userLocale, ['bio'] as const, preferences).fields.bio;
}
